/* eslint-disable react-hooks/exhaustive-deps */
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  selectNewVehicle,
  setCar,
  setId,
  setMake,
  setModel,
  setYear,
} from '../features/car/carSlice';
import useAuthentication from '../hooks/useAuthentication';
import styles from '../sass/components/CarSelector.module.scss';
import {
  GET_MAKES_BY_YEAR,
  GET_MODELS_BY_YEAR_AND_MAKE,
  GET_VEHICLES,
  GET_EXACT_VEHICLES_BY_MAKE_MODEL_AND_YEAR,
  GET_VEHICLES_BY_MAKE_MODEL_AND_YEAR,
  GET_VEHICLES_BY_MAKE_MODEL_AND_YEAR_AND_ENGINE,
  GET_YEARS,
  FIND_ALL_USER_VEHICLES_BY_CURRENT_USER,
  FIND_ALL_USER_VEHICLES_BY_USER_ID,
} from '../util/gql';
import Field from './Field';
import PreviousSelector, { Selection } from './PreviousSelector';
import { ReactComponent as Search } from '../images/search.svg';
import TextInput from './TextInput';
import Selector from './Selector';
import { decodeVin } from '../util/decodeVin';
import { UserVehicle } from '../interfaces/UserVehicle';
import { Vehicle } from '../interfaces/Vehicle';
import { Subscription } from '../interfaces/Subscription';
import Button from './Button';
import Alert from './Alert';
import { clearAllAddOns } from '../features/addOns/addOnsSlice';
import {
  setStoredDiscountAmount,
  setStoredDiscountCodeId,
  setStoredDiscountType,
  setStoredPromoCreditAmount,
} from '../features/booking/bookingSlice';
import { getDistinctId, mp } from '../util/analytics';
import { aprTrack } from '../util/aperioTracking';
import { clearContact } from '../features/serviceLocation/serviceLocationSlice';
import CameraAccessOverlay from './CameraAccessOverlay';
import ModalCloseButton from './ModalCloseButton';
import { useTenant } from './TenantContext';
import { normalizeThemePreset } from '../util/themePreset';
import { vehicleNameCase } from '../util/vehicleText';
import { engineLabels } from '../util/engineLabel';

export default function CarSelector() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // Aperio shows "Toyota", not "TOYOTA" (display only; QA #49).
  const themed = normalizeThemePreset(useTenant()?.themePreset) !== 'default';
  // The engine choices. Under a preset they read "1.5L 4-cyl turbo", not
  // the catalogue's "1.5L 4-CYL ENGINE CODE L15B7 9 TURBO" (util/engineLabel);
  // every other tenant keeps the catalogue's names.
  const engineOptions = (
    rows?: { id: number; engine: string }[] | null,
  ): { value: number; label: string }[] => {
    if (!rows) return [];
    const labels = themed
      ? engineLabels(rows.map((v) => v.engine))
      : rows.map((v) => v.engine);
    return rows.map((v, i) => ({ value: v.id, label: labels[i] }));
  };
  const nameLabel = (text: string) => (themed ? vehicleNameCase(text) : text);
  const { loggedIn: isLoggedIn } = useAuthentication();
  const car = useAppSelector((state) => state.car);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [filter, setFilter] = useState<string>('');
  // How the VIN in the box got there, for the Aperio vehicle_identified event.
  const vinMethod = useRef<'vin' | 'scan'>('vin');
  const [filteredVehicles, setFilteredVehicles] = useState<Vehicle[]>([]);
  const [exactMatch, setExactMatch] = useState<boolean>(false);
  const [, setVinDecoded] = useState<boolean>(false);
  const [searchMode, setSearchMode] = useState('MakeModel');
  const [showVinInfo, setShowVinInfo] = useState(false);
  const [vinLookup, setVinLookup] = useState<'idle' | 'loading' | 'failed'>('idle');
  const [vinLookupNonce, setVinLookupNonce] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const currentUser = useAppSelector((
    state,
  ) => state.auth.currentUser);
  const customerId = useAppSelector((state) => state.customer.id);

  const { data: yearData } = useQuery(GET_YEARS);

  const [loadMakeData, { data: makeData }] = useLazyQuery(GET_MAKES_BY_YEAR);

  useEffect(() => {
    if (car.year !== '') {
      loadMakeData({
        variables: {
          year: car.year,
        },
      });
    }
  }, [car.year, loadMakeData]);

  const [
    loadModelData,
    { data: modelData },
  ] = useLazyQuery(GET_MODELS_BY_YEAR_AND_MAKE);

  useEffect(() => {
    if (car.year !== '' && car.make !== '') {
      loadModelData({
        variables: {
          year: car.year,
          make: car.make,
        },
      });
    }
  }, [car.year, car.make, loadModelData]);

  const [
    loadVehicleData,
    { data: vehicleData },
  ] = useLazyQuery(GET_EXACT_VEHICLES_BY_MAKE_MODEL_AND_YEAR);

  const [
    loadVehicleDataVin,
    { data: vehicleDataVin },
  ] = useLazyQuery(GET_VEHICLES_BY_MAKE_MODEL_AND_YEAR);

  const [
    loadAllVehicleData,
  ] = useLazyQuery(GET_VEHICLES_BY_MAKE_MODEL_AND_YEAR_AND_ENGINE,
    { fetchPolicy: 'no-cache' });

  useEffect(() => {
    if (car.make !== ''
      && car.model !== ''
      && car.year !== ''
      && searchMode === 'MakeModel') {
      loadVehicleData({
        variables: {
          make: car.make,
          model: car.model,
          year: parseInt(car.year, 10),
        },
      });
    }
  }, [car.make, car.model, car.year, loadVehicleData]);

  useEffect(() => {
    if (car.make !== ''
      && car.model !== ''
      && car.year !== ''
      && searchMode === 'VIN') {
      loadVehicleDataVin({
        variables: {
          make: car.make,
          model: car.model,
          year: parseInt(car.year, 10),
        },
      });
    }
  }, [car.make, car.model, car.year, loadVehicleDataVin]);

  const {
    loading: userVehicleLoading,
    error: userVehicleError,
  } = useQuery(GET_VEHICLES, {
    skip: !isLoggedIn,
    fetchPolicy: 'network-only',
  });

  const getActiveSubscriptionId = (
    userVehicleId: number,
    subscriptions: Subscription[] = [],
  ) => {
    const userVehicleSubscription = subscriptions.find(
      (subscription) => subscription.userVehicleId
        === userVehicleId
        && subscription.status === 'ACTIVE',
    );
    return userVehicleSubscription ? userVehicleSubscription.id : undefined;
  };

  const createVehicleArray = (
    userVehicles: UserVehicle[],
  ): Vehicle[] => userVehicles.map((item) => ({
    id: item.vehicle.id,
    make: item.vehicle.make,
    model: item.vehicle.model,
    year: item.vehicle.year,
    userVehicleId: item.id,
    vin: (item.vin ? item.vin : ''),
    name: (item.name ? item.name : ''),
    recommendedOil: item.vehicle.recommendedOil,
    oilVolume: item.vehicle.oilVolume,
    activeSubscriptionId: getActiveSubscriptionId(item.id, item.subscriptions),
    subscriptions: item.subscriptions,
  }));

  const { data: userVehicles } = useQuery(
    FIND_ALL_USER_VEHICLES_BY_CURRENT_USER, {
      onCompleted: (data) => {
        const arr = createVehicleArray(data.findAllUserVehiclesByCurrentUser);
        setVehicles(arr);
      },
      skip: !isLoggedIn
      || currentUser.roles?.includes('manager')
      || currentUser.roles?.includes('technician')
      || currentUser.roles?.includes('tester')
      || currentUser.roles?.includes('sales'),
      fetchPolicy: 'network-only',
    },
  );

  const { data: userVehiclesById } = useQuery(
    FIND_ALL_USER_VEHICLES_BY_USER_ID, {
      variables: {
        userId: customerId,
      },
      onCompleted: (data) => {
        const arr = createVehicleArray(data.findAllUserVehiclesByUserId);
        setVehicles(arr);
      },
      skip: (!currentUser.roles?.includes('manager')
      && !currentUser.roles?.includes('technician')
      && !currentUser.roles?.includes('tester')
      && !currentUser.roles?.includes('sales')),
      fetchPolicy: 'network-only',
    },
  );

  const decodeVinFromImage = (capturedImage: any):
  Promise<string> => new Promise((resolve, reject) => {
    const imgURL = URL.createObjectURL(capturedImage);
    const img = new Image();
    img.src = imgURL;

    img.onload = async () => {
      try {
        // The barcode reader is ~400 KB: fetched on the first scan, never
        // as part of the booking step (QA 2026-09-25 #14).
        const { BrowserMultiFormatReader } = await import(
          /* webpackChunkName: "scanner" */ '@zxing/browser'
        );
        const codeReader = new BrowserMultiFormatReader();
        const result = await codeReader.decodeFromImageElement(img);
        let vinNumber = result.getText();
        // Handle VINs that might have extra characters
        if (vinNumber.length > 17) {
          vinNumber = vinNumber.slice(1);
        }
        resolve(vinNumber);
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(imgURL);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(imgURL);
      reject(new Error('Failed to load image for decoding'));
    };
  });

  const handleFilterChange = (value: string) => {
    if (errorMessage) {
      setErrorMessage('');
    }
    if (value && value.length === 17) {
      // That a full VIN was entered - never the VIN itself.
      mp.track('Vehicle - VIN Entered', {
        is_logged_in: !!isLoggedIn,
        anon_id: getDistinctId(),
        path: window.location.pathname,
      });
    }
    setFilter(value);
  };

  const handleVinScan = async (capturedImage: any) => {
    setIsScanning(true);
    setErrorMessage('');
    try {
      const scannedVin = await decodeVinFromImage(capturedImage);
      if (scannedVin && scannedVin.length === 17) {
        vinMethod.current = 'scan';
        handleFilterChange(scannedVin);
        mp.track('Vehicle - VIN Scanned Successfully', {
          is_logged_in: !!isLoggedIn,
          anon_id: getDistinctId(),
          path: window.location.pathname,
        });
      } else {
        setErrorMessage(t('vehicle.vin.decodeFailed'));
        mp.track('Vehicle - VIN Scan Failed', {
          error: 'Invalid VIN length',
          is_logged_in: !!isLoggedIn,
          anon_id: getDistinctId(),
          path: window.location.pathname,
        });
      }
    } catch (error) {
      setErrorMessage(t('vehicle.vin.scanFailed'));
      mp.track('Vehicle - VIN Scan Failed', {
        error: 'Decode error',
        is_logged_in: !!isLoggedIn,
        anon_id: getDistinctId(),
        path: window.location.pathname,
      });
    } finally {
      setIsScanning(false);
    }
  };

  const selectMake = useCallback((make) => {
    if (make !== car.make) {
      dispatch(setMake(make));
    }
  }, [car.make, dispatch]);

  const handleMakeChange = (event: FormEvent<HTMLSelectElement>) => {
    selectMake(event.currentTarget.value);
    mp.track(
      'Vehicle - New Vehicle Year and Make Selected', {
        vehicle_make: event.currentTarget.value,
        vehicle_year: car.year,
        is_logged_in: !!isLoggedIn,
        anon_id: getDistinctId(),
        path: window.location.pathname,
      },
    );
  };

  const selectModel = useCallback((model) => {
    if (model !== car.model) {
      dispatch(setModel(model));
    }
  }, [car.model, dispatch]);

  const handleModelChange = (event: FormEvent<HTMLSelectElement>) => {
    selectModel(event.currentTarget.value);
    mp.track(
      'Vehicle - New Vehicle Year, Make and Model Selected', {
        vehicle_model: event.currentTarget.value,
        vehicle_make: car.make,
        vehicle_year: car.year,
        is_logged_in: !!isLoggedIn,
        anon_id: getDistinctId(),
        path: window.location.pathname,
      },
    );
  };

  const selectYear = useCallback((year) => {
    if (year !== car.year) {
      dispatch(setYear(year));
    }
  }, [car.year, dispatch]);

  const handleYearChange = (event: FormEvent<HTMLSelectElement>) => {
    selectYear(event.currentTarget.value);
    mp.track(
      'Vehicle - New Vehicle Year Selected', {
        vehicle_year: event.currentTarget.value,
        is_logged_in: !!isLoggedIn,
        anon_id: getDistinctId(),
        path: window.location.pathname,
      },
    );
  };

  const selectId = useCallback((id) => {
    if (id !== car.id) {
      dispatch(setId(id));
    }
  }, [car.id, dispatch]);

  const handleIdChange = (event: FormEvent<HTMLSelectElement>) => {
    selectId(parseInt(event.currentTarget.value, 10));

    const { selectedIndex, value } = event.currentTarget;
    // The catalogue's engine name, as reported before the preset's readable
    // labels (engineOptions) existed.
    const selectedId = parseInt(value, 10);
    const selectedText = [
      ...(vehicleData?.getExactVehiclesByMakeModelAndYear || []),
      ...(vehicleDataVin?.getVehiclesByMakeModelAndYear || []),
    ].find((v: { id: number }) => v.id === selectedId)?.engine
      ?? event.currentTarget.options[selectedIndex].text;
    aprTrack('vehicle_identified', {
      year: car.year, make: car.make, model: car.model, method: 'picker',
    });
    mp.track(
      'Vehicle - New Vehicle Year, Make and Model Selected', {
        vehicle_engine: selectedText,
        vehicle_model: car.model,
        vehicle_make: car.make,
        vehicle_year: car.year,
        is_logged_in: !!isLoggedIn,
        anon_id: getDistinctId(),
        path: window.location.pathname,
      },
    );
  };

  const handleSelectVehicle = (selected: Selection) => {
    if (selected === 'unset' || selected === 'new') {
      dispatch(selectNewVehicle());
    } else {
      const arrVehicles = filter ? filteredVehicles : vehicles;
      dispatch(setCar({
        ...arrVehicles[selected],
        selected,
      }));
      dispatch(clearAllAddOns());
      dispatch(setStoredDiscountAmount(null));
      dispatch(setStoredDiscountCodeId(null));
      dispatch(setStoredDiscountType(null));
      dispatch(setStoredPromoCreditAmount(null));
      dispatch(clearContact());
      setVinDecoded(false);

      aprTrack('vehicle_identified', {
        year: arrVehicles[selected].year,
        make: arrVehicles[selected].make,
        model: arrVehicles[selected].model,
        method: 'picker',
      });
      mp.track(
        'Vehicle - Customer selected vehicle from customer vehicle list', {
          vehicle_year: arrVehicles[selected].year,
          vehicle_make: arrVehicles[selected].make,
          vehicle_model: arrVehicles[selected].model,
          vehicle_engine: arrVehicles[selected].engine,
          is_logged_in: !!isLoggedIn,
          anon_id: getDistinctId(),
          path: window.location.pathname,
        },
      );
    }
  };

  useEffect(() => {
    if (car.selected === 'unset' && vehicles.length > 0
        && (userVehicles || userVehiclesById)) {
      dispatch(setCar({
        ...vehicles[0],
        selected: 0,
      }));
      dispatch(clearAllAddOns());
      dispatch(setStoredDiscountAmount(null));
      dispatch(setStoredDiscountCodeId(null));
      dispatch(setStoredDiscountType(null));
      dispatch(setStoredPromoCreditAmount(null));
    }
  }, [dispatch, car.selected, vehicles, userVehicles, userVehiclesById]);

  const handleNameInputChange = (event: FormEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    dispatch(setCar({ ...car, name: value }));
    dispatch(clearAllAddOns());
    dispatch(setStoredDiscountAmount(null));
    dispatch(setStoredDiscountCodeId(null));
    dispatch(setStoredDiscountType(null));
    dispatch(setStoredPromoCreditAmount(null));
  };

  // A new VIN search starts from a blank vehicle. Not on the way in: this
  // used to run on mount too, so Back, Forward or a reload of this step
  // wiped the vehicle the booking already had (and with it the plan), and
  // the schedule step then booked vehicleId null for $0 (QA 2026-09-25 #9).
  const lastFilter = useRef(filter);
  useEffect(() => {
    if (lastFilter.current === filter) return;
    lastFilter.current = filter;
    dispatch(setCar({
      year: '',
      make: '',
      model: '',
      id: null,
      userVehicleId: undefined,
      vin: '',
      selected: 'new',
      name: '',
    }));
    setFilteredVehicles([]);
    setExactMatch(false);
    setVinDecoded(false);
    dispatch(clearAllAddOns());
    dispatch(setStoredDiscountAmount(null));
    dispatch(setStoredDiscountCodeId(null));
    dispatch(setStoredDiscountType(null));
    dispatch(setStoredPromoCreditAmount(null));
  }, [filter, dispatch]);

  useEffect(() => {
    const filtered = vehicles.filter((vehicle) => vehicle.vin
      ?.toLowerCase()
      .includes(filter.toLowerCase()));
    setFilteredVehicles(filtered);

    if (filtered.length === 1 && filter.length === 17) {
      setExactMatch(true);
      dispatch(setCar({
        ...filtered[0],
        selected: 0,
      }));
      dispatch(clearAllAddOns());
      dispatch(setStoredDiscountAmount(null));
      dispatch(setStoredDiscountCodeId(null));
      dispatch(setStoredDiscountType(null));
      dispatch(setStoredPromoCreditAmount(null));
    } else {
      setExactMatch(false);
    }
  }, [filter, vehicles, dispatch]);

  useEffect(() => {
    if (filter.length === 17 && filteredVehicles.length === 0
      && car.vin !== filter) {
      const checkAndSetVehicle = async (decodedVehicle: any) => {
        const {
          ModelYear: rawYear,
          Make: make,
          Model,
          DisplacementL,
          EngineCylinders,
        } = decodedVehicle;

        const model = Model.toLowerCase().includes('promaster') ? 'promaster'
          : Model;

        const engine = `${DisplacementL}L ${EngineCylinders}-cyl`;
        const year = parseInt(rawYear, 10);
        let existingVehicle = null;
        if (year) {
          existingVehicle = await loadAllVehicleData({
            variables: {
              make,
              model,
              year,
              engine,
            },
          });
        }
        // Year / make / model / engine under the vehicle_* names every
        // other vehicle event uses; never the VIN.
        const trackingProps = {
          vehicle_year: year,
          vehicle_make: make,
          vehicle_model: model,
          vehicle_engine: engine,
        };
        if (existingVehicle?.data?.getVehiclesByMakeModelAndYearAndEngine) {
          if (existingVehicle.data.getVehiclesByMakeModelAndYearAndEngine.model
            === 'NOT LISTED') {
            mp.track('VIN Decode Failed', {
              ...trackingProps,
              error_message: 'Vehicle not in database',
            });
          } else {
            mp.track('Vehicle Selected', trackingProps);
            // Aperio contract event; year / make / model only, never the VIN.
            aprTrack('vehicle_identified', {
              year, make, model, method: vinMethod.current,
            });
            vinMethod.current = 'vin';
          }
        } else {
          mp.track('VIN Decode Failed', {
            ...trackingProps,
            error_message: 'Vehicle not in database',
          });
        }

        if (existingVehicle && existingVehicle.data
           && existingVehicle.data.getVehiclesByMakeModelAndYearAndEngine) {
          const {
            year: existingYear,
            make: existingMake,
            model: existingModel,
          } = existingVehicle.data.getVehiclesByMakeModelAndYearAndEngine;

          dispatch(
            setCar({
              ...car,
              year: existingYear,
              make: existingMake,
              model: existingModel,
              vin: filter,
            }),
          );
          dispatch(clearAllAddOns());
          dispatch(setStoredDiscountAmount(null));
          dispatch(setStoredDiscountCodeId(null));
          dispatch(setStoredDiscountType(null));
          dispatch(setStoredPromoCreditAmount(null));
        } else {
          const notListedMake = 'Vehicle Not listed';
          const notListedModel = 'NOT LISTED';
          const notListedYear = 2024;

          mp.track('VIN Decode Failed', {
            ...trackingProps,
            error_message: 'Vehicle not in database',
          });

          dispatch(
            setCar({
              ...car,
              year: notListedYear.toString(),
              make: notListedMake,
              model: notListedModel,
              vin: filter,
              selected: 'new',
            }),
          );
          dispatch(clearAllAddOns());
          dispatch(setStoredDiscountAmount(null));
          dispatch(setStoredDiscountCodeId(null));
          dispatch(setStoredDiscountType(null));
          dispatch(setStoredPromoCreditAmount(null));
        }

        setVinDecoded(true);
      };
      if (filter.length === 17 && car.vin !== filter) {
        setVinLookup('loading');
        decodeVin(filter)
          .then(async (decodedVehicle) => {
            if (decodedVehicle) {
              await checkAndSetVehicle(decodedVehicle);
            }
            setVinLookup('idle');
          })
          .catch(() => {
            // A rejected promise used to vanish silently here, which read as
            // "VIN lookup does nothing". Say so and point at the fallback.
            setVinLookup('failed');
            setErrorMessage(t('vehicle.vin.lookupFailed'));
          });
      }
    } else {
      setExactMatch(false);
    }
  // vinLookupNonce lets the "Look up" button re-run the decode on demand.
  }, [filter, filteredVehicles, dispatch, car, vinLookupNonce, t]);

  const lookupTypedVin = () => {
    const vin = filter.trim();
    if (vin.length !== 17) {
      setErrorMessage(t('vehicle.vin.length', { count: vin.length }));
      return;
    }
    setErrorMessage('');
    // Clear the stored VIN so the effect above treats this as a new lookup.
    dispatch(setCar({ ...car, vin: '' }));
    setVinLookupNonce((n) => n + 1);
  };

  const vehicleNames = filteredVehicles.map((vehicle: {
    make: string; model: string; year: string; vin?: string
  }) => `${vehicle.make} ${vehicle.model} ${vehicle.year}`);

  const vinNames = filteredVehicles.map((vehicle: {
    vin?: string
  }) => `${vehicle.vin}`);

  return (
    <div className={styles.carSelector}>
      <h4 aria-level={2}>{t('vehicle.title')}</h4>
      <p>{t('vehicle.intro')}</p>
      <div className={styles.toggleContainer}>
        <Button
          onClick={() => {
            setSearchMode('MakeModel');
            dispatch(selectNewVehicle());
            handleFilterChange('');
          }}
          className={searchMode === 'MakeModel' ? styles.active : ''}
        >
          {t('vehicle.makeModel')}
        </Button>
        <Button
          onClick={() => {
            setSearchMode('VIN');
            dispatch(selectNewVehicle());
          }}
          className={searchMode === 'VIN' ? styles.active : ''}
        >
          {t('vehicle.vinLookup')}
        </Button>
      </div>
      {searchMode === 'VIN' && (
      <>
        <br />
        <p>{t('vehicle.vin.prompt')}</p>
        <div className={styles.headerContainer}>
          <div className={styles.searchBar}>
            <div className={styles.magnifyingGlass}>
              <Search />
            </div>
            <TextInput
              placeholder={t('vehicle.vin.placeholder')}
              maxlength={17}
              value={filter}
              onChange={(e) => handleFilterChange(
                e.currentTarget.value.toUpperCase(),
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  lookupTypedVin();
                }
              }}
            />
          </div>
          <div className={styles.searchBar}>
            <Button
              variant="primary"
              onClick={lookupTypedVin}
              loading={vinLookup === 'loading'}
            >
              {t('vehicle.vin.lookUp')}
            </Button>
            <CameraAccessOverlay
              onCapture={handleVinScan}
              className={styles.vinScanButton}
              buttonLabel={t('vehicle.vin.scan')}
            />
            <Button
              variant="icon"
              onClick={() => setShowVinInfo(true)}
              aria-label={t('product.info')}
            >
              <InfoOutlinedIcon fontSize="small" />
            </Button>
          </div>
        </div>

        { errorMessage && (
        <Alert
          message={errorMessage}
          type="error"
        />
        )}

        {showVinInfo && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              <ModalCloseButton onClose={() => setShowVinInfo(false)} />
              <h3>{t('vehicle.vinHelp.title')}</h3>
              <ol>
                <li>
                  <strong>{t('vehicle.vinHelp.locate')}</strong>
                  <p>{t('vehicle.vinHelp.locateBody')}</p>
                </li>
                <li>
                  <strong>{t('vehicle.vinHelp.align')}</strong>
                  <ul>
                    <li>{t('vehicle.vinHelp.alignDistance')}</li>
                    <li>{t('vehicle.vinHelp.alignGuide')}</li>
                    <li>{t('vehicle.vinHelp.alignCenter')}</li>
                  </ul>
                </li>
                <li>
                  <strong>{t('vehicle.vinHelp.capture')}</strong>
                  <p>{t('vehicle.vinHelp.captureBody')}</p>
                </li>
                <li>
                  <p>
                    <Trans
                      t={t}
                      i18nKey="vehicle.vinHelp.tip"
                      components={{ b: <strong /> }}
                    />
                  </p>
                </li>
              </ol>
              <div className={styles.modalActions}>
                <Button
                  variant="primary"
                  onClick={() => setShowVinInfo(false)}
                  className={styles.closeButton}
                >
                  {t('common.close')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {isScanning && (
          <p className={styles.scanningMessage}>{t('vehicle.vin.scanning')}</p>
        )}
        {vinLookup === 'loading' && (
          <p className={styles.scanningMessage}>{t('vehicle.vin.lookingUp')}</p>
        )}

        <PreviousSelector
          subtitleNames={vinNames}
          data={vehicleNames}
          newLabel={vehicleNames.length
            ? t('vehicle.addAnother') : t('vehicle.yourVehicle')}
          loading={userVehicleLoading}
          error={!!userVehicleError}
          name="vehicle"
          selected={car.selected}
          onChange={handleSelectVehicle}
          hideNewOption={exactMatch}
        >
          <Field
            id="year"
            label={t('vehicle.fields.year')}
          >
            <Selector
              id="year"
              label={t('vehicle.fields.year')}
              placeholder={t('vehicle.placeholders.year')}
              value={car.year}
              onChange={handleYearChange}
              disabled={car.year === ''}
              options={yearData?.getAllVehicleYears?.map((m: {
              year: string;
            }) => ({
                value: m.year,
                label: m.year,
              })) || []}
            />
          </Field>
          <Field
            id="make"
            label={t('vehicle.fields.make')}
          >
            <Selector
              id="make"
              label={t('vehicle.fields.make')}
              placeholder={t('vehicle.placeholders.make')}
              value={car.make}
              disabled={car.year === ''}
              onChange={handleMakeChange}
              options={makeData?.getMakesByYear?.map((m: {
              make: string;
            }) => ({
                value: m.make,
                label: nameLabel(m.make),
              })) || []}
            />
          </Field>
          <Field
            id="model"
            label={t('vehicle.fields.model')}
          >
            <Selector
              id="model"
              label={t('vehicle.fields.model')}
              placeholder={t('vehicle.placeholders.model')}
              value={car.model}
              disabled={car.make === ''}
              onChange={handleModelChange}
              options={modelData?.getModelsByYearAndMake?.map((m: {
              model: string;
            }) => ({
                value: m.model,
                label: nameLabel(m.model),
              })) || []}
            />
          </Field>
          <Field
            id="engine"
            label={t('vehicle.fields.engine')}
          >
            <Selector
              id="engine"
              label={t('vehicle.fields.engine')}
              placeholder={t('vehicle.placeholders.engine')}
              value={car.id || ''}
              disabled={car.make === '' || car.model === '' || car.year === ''}
              onChange={handleIdChange}
              options={engineOptions(
                vehicleDataVin?.getVehiclesByMakeModelAndYear,
              )}
            />
          </Field>
        </PreviousSelector>
      </>
      )}
      {searchMode === 'MakeModel' && (
        <>

          <PreviousSelector
            subtitleNames={vinNames}
            data={vehicleNames}
            newLabel={vehicleNames.length
              ? t('vehicle.addAnother') : t('vehicle.yourVehicle')}
            loading={userVehicleLoading}
            error={!!userVehicleError}
            name="vehicle"
            selected={car.selected}
            onChange={handleSelectVehicle}
            hideNewOption={exactMatch}
          >
            <p className={styles.subtitle}>{t('vehicle.selectYmm')}</p>
            <Field
              id="year"
              label={t('vehicle.fields.year')}
            >
              <Selector
                id="year"
                label={t('vehicle.fields.year')}
                placeholder={t('vehicle.placeholders.year')}
                value={car.year}
                onChange={handleYearChange}
                options={yearData?.getAllVehicleYears?.map((m: {
              year: string;
            }) => ({
                  value: m.year,
                  label: m.year,
                })) || []}
              />
            </Field>
            <Field
              id="make"
              label={t('vehicle.fields.make')}
            >
              <Selector
                id="make"
                label={t('vehicle.fields.make')}
                placeholder={t('vehicle.placeholders.make')}
                value={car.make}
                disabled={car.year === ''}
                onChange={handleMakeChange}
                options={makeData?.getMakesByYear?.map((m: {
              make: string;
            }) => ({
                  value: m.make,
                  label: nameLabel(m.make),
                })) || []}
              />
            </Field>
            <Field
              id="model"
              label={t('vehicle.fields.model')}
            >
              <Selector
                id="model"
                label={t('vehicle.fields.model')}
                placeholder={t('vehicle.placeholders.model')}
                value={car.model}
                disabled={car.make === ''}
                onChange={handleModelChange}
                options={modelData?.getModelsByYearAndMake?.map((m: {
              model: string;
            }) => ({
                  value: m.model,
                  label: nameLabel(m.model),
                })) || []}
              />
            </Field>
            <Field
              id="engine"
              label={t('vehicle.fields.engine')}
            >
              <Selector
                id="engine"
                label={t('vehicle.fields.engine')}
                placeholder={t('vehicle.placeholders.engine')}
                value={car.id || ''}
                disabled={car.make === ''
                  || car.model === ''
                  || car.year === ''}
                onChange={handleIdChange}
                options={engineOptions(
                  vehicleData?.getExactVehiclesByMakeModelAndYear,
                )}
              />
            </Field>
          </PreviousSelector>
        </>
      )}

      {car.id && (
      <Field
        id="name"
        // One label, not two stacked ones, under the preset (QA #49).
        label={themed ? t('vehicle.nameOptional') : 'Name'}
      >
        <TextInput
          id="name"
          title={themed ? undefined : 'Optional Vehicle Information'}
          placeholder={t('vehicle.namePlaceholder')}
          type="text"
          value={car.name}
          disabled={car.make === '' || car.model === '' || car.year === ''}
          onChange={handleNameInputChange}
        />
      </Field>
      )}
    </div>
  );
}
